import { useEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  ArrowUpToLine,
  FileCheck,
  MessageCircleQuestion,
  RotateCcw,
  Square,
  X,
  Zap,
} from "lucide-react";
import { BriefStateChip } from "@/components/grooming/brief-state-chip";
import { StageChip } from "@/components/grooming/grooming-chips";
import { Button } from "@/components/ui/button";
import { formatElapsed, RUN_PHASE_LABELS } from "@/components/grooming/run-activity";
import { groomingKeys, useGroomingBatch } from "@/hooks/queries/use-grooming";
import { useAuthorQuestionsDraft, workItemKeys } from "@/hooks/queries/use-work-items";
import type { GroomingBatchItemView, GroomingQueueEntry, GroomingQueueEntryKind } from "@ttc-spectree/toolbox-contract";
import { groomingErrorMessage } from "@/lib/grooming/error-messages";
import { localTimeOf } from "@/lib/grooming/local-time";
import { groomingPath, projectPath } from "@/lib/grooming/paths";
import { FINISHED_TEXT, briefStateOf, confidenceWords } from "@/lib/grooming/plain-words";
import { questionsPostedAt, queueTitle } from "@/lib/grooming/queue-titles";
import { cn } from "@/lib/utils";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";

/**
 * One row of the Queue screen (epic PI-SPEC-SW-E284, feature
 * PI-SPEC-SW-1439): a running entry (phase, activity, elapsed timer, Stop),
 * an Up next entry (its place, Move up, Move down, Remove) or a finished
 * entry (not ready, failed in red with Retry, stopped with Retry, or a
 * finished product-areas draft). The elapsed timer ticks inside its own
 * child, so the list does not re-render every second.
 *
 * A finished brief or author-questions entry is a result card (epic
 * PI-SPEC-SW-E289, feature PI-SPEC-SW-1474, UX-PM-08 and UX-PM-26, Claude
 * Design frame 1c): the title, what was made and when, the size and
 * confidence (from the batch read, a database read with no ADO call), the
 * brief's state and Open brief. The whole card opens the item's reading pane.
 * An author-questions card shows its question count only when this page
 * already read the draft (the draft read checks the item live in ADO, so a
 * card never starts it).
 *
 * Up next rows (epic PI-SPEC-SW-E290, feature PI-SPEC-SW-1479, frame B1,
 * UX-PM-04): a single item that jumped the queue is "Next in line" with where
 * it came from and how many session items it went ahead of, and only Remove;
 * a run stopped by sleep, Sign Out or quit says "Stopped at 4:12 PM · goes
 * first"; every row after the first offers "Move to top" on hover and focus.
 */

export type QueueSection = "running" | "upNext" | "completed";

/** What a row can ask the queue to do; each resolves once the app answered. */
export interface QueueRowActions {
  stop: (entryId: string) => void;
  remove: (entryId: string) => void;
  move: (entryId: string, toIndex: number) => void;
  retry: (entryId: string) => void;
}

/** Plain names of what an entry runs. */
export const QUEUE_KIND_LABELS: Readonly<Record<GroomingQueueEntryKind, string>> = {
  "groom-item": "Brief",
  "product-areas": "Product areas draft",
  "author-questions": "Author questions draft",
};

/** The fallback title of an author-questions entry whose item title is not known. */
export const AUTHOR_QUESTIONS_FALLBACK_TITLE = "Questions for the author";

/**
 * Where a finished entry's result lives: the item for briefs and author
 * questions, Project → Product areas for product areas (PKG-PM-F).
 */
export function queueEntryLink(entry: GroomingQueueEntry): string {
  if (entry.kind === "product-areas" || !entry.itemId) return projectPath(entry.project, "product-areas");
  return groomingPath(entry.project, `items/${entry.itemId}`);
}

/** The `data` of a cached `{ data }` response, else the value itself. */
function unwrap(raw: unknown): unknown {
  return raw !== null && typeof raw === "object" && "data" in raw ? (raw as { data: unknown }).data : raw;
}

function titleField(value: unknown): string | null {
  if (value === null || typeof value !== "object" || !("title" in value)) return null;
  const title = (value as { title: unknown }).title;
  return typeof title === "string" && title.trim() ? title.trim() : null;
}

/**
 * An item's title from what this page already read: the item read
 * (`workItemKeys.detail`), else any cached backlog read of the project.
 * Never starts a read. Exported for tests.
 */
export function cachedItemTitle(queryClient: QueryClient, project: string, itemId: string): string | null {
  const detail = titleField(unwrap(queryClient.getQueryData(workItemKeys.detail("ado", project, itemId))));
  if (detail) return detail;
  const backlogs = queryClient.getQueriesData({
    queryKey: [...groomingKeys.project(project), "backlog"],
  });
  for (const [, raw] of backlogs) {
    const backlog = unwrap(raw);
    if (backlog === null || typeof backlog !== "object") continue;
    const { items, exceptions } = backlog as { items?: unknown; exceptions?: unknown };
    const rows: unknown[] = [
      ...(Array.isArray(items) ? (items as unknown[]) : []),
      ...(Array.isArray(exceptions) ? (exceptions as unknown[]) : []),
    ];
    const row = rows.find(
      (candidate) =>
        candidate !== null && typeof candidate === "object" && (candidate as { itemId?: unknown }).itemId === itemId
    );
    const title = titleField(row);
    if (title) return title;
  }
  return null;
}

/** A row's title, and whether it is only the item's ID (then the separate ID is not shown again). */
export interface EntryTitle {
  title: string;
  idOnly: boolean;
}

/**
 * The row's title (UX-PM-26): the app's title, else the title this computer
 * remembered when it queued the entry, else `known` (for example the batch
 * item's title), else a cached item or backlog read, else what it is.
 * Never the ID twice: when the fallback is `AB#id`, `idOnly` is true.
 */
export function resolveEntryTitle(
  entry: GroomingQueueEntry,
  queryClient: QueryClient | null,
  known?: string | null
): EntryTitle {
  const own = entry.title?.trim();
  if (own) return { title: own, idOnly: false };
  if (entry.kind === "product-areas") return { title: `Product areas · ${entry.project}`, idOnly: false };
  const remembered = queueTitle(entry.key);
  if (remembered) return { title: remembered, idOnly: false };
  if (known?.trim()) return { title: known.trim(), idOnly: false };
  if (!entry.itemId) return { title: entry.project, idOnly: false };
  const cached = queryClient ? cachedItemTitle(queryClient, entry.project, entry.itemId) : null;
  if (cached) return { title: cached, idOnly: false };
  if (entry.kind === "author-questions") return { title: AUTHOR_QUESTIONS_FALLBACK_TITLE, idOnly: false };
  return { title: `AB#${entry.itemId}`, idOnly: true };
}

/** {@link resolveEntryTitle} with the page's query cache. */
function useEntryTitle(entry: GroomingQueueEntry, known?: string | null): EntryTitle {
  const queryClient = useQueryClient();
  return resolveEntryTitle(entry, queryClient, known);
}

/** "11:42 AM" for a finished entry, or null when the time is missing or unreadable. Pure; exported for tests. */
export function finishedTimeText(finishedAt: string | null): string | null {
  if (!finishedAt) return null;
  const at = new Date(finishedAt);
  if (Number.isNaN(at.getTime())) return null;
  return at.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

/** "About 3 points, could be 2–5" (no range when low and high are the same). Pure; exported for tests. */
export function briefSizeText(brief: { suggestedPoints: number; pointsLow: number; pointsHigh: number }): string {
  const points = `${String(brief.suggestedPoints)} ${brief.suggestedPoints === 1 ? "point" : "points"}`;
  const range =
    brief.pointsLow === brief.pointsHigh ? "" : `, could be ${String(brief.pointsLow)}–${String(brief.pointsHigh)}`;
  return `About ${points}${range}`;
}

/** "4 questions drafted, not posted" (or "posted"); without a count, "Questions drafted, …". Pure; exported for tests. */
export function questionsDraftedText(count: number | null, posted: boolean): string {
  const state = posted ? "posted" : "not posted";
  if (count === null || count <= 0) return `Questions drafted, ${state}`;
  return `${String(count)} ${count === 1 ? "question" : "questions"} drafted, ${state}`;
}

/** True for a finished brief or author-questions entry with an item: it renders as a result card and opens the item. */
export function isResultEntry(entry: GroomingQueueEntry): boolean {
  return (
    Boolean(entry.itemId) && entry.kind !== "product-areas" && (entry.state === "briefed" || entry.state === "done")
  );
}

/** Whole minutes since `iso` ("3 min ago", "just now"); null for an unreadable time. Pure; exported for tests. */
export function minutesAgoText(iso: string | null | undefined, now: number = Date.now()): string | null {
  if (!iso) return null;
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return null;
  const minutes = Math.max(0, Math.floor((now - at) / 60_000));
  return minutes === 0 ? "just now" : `${String(minutes)} min ago`;
}

/**
 * A Next in line row's line: "Added 3 min ago · went ahead of the 5 waiting
 * session items". The entry does not say where it was added (One item and
 * the reading pane's "Groom this item" both add single items), so the line
 * names no page. The "went ahead" part is left out when the app sends no
 * count or 0. Pure; exported for tests.
 */
export function nextInLineText(entry: GroomingQueueEntry, now: number = Date.now()): string {
  const ago = minutesAgoText(entry.queuedAt, now);
  const head = ago ? `Added ${ago}` : "Added";
  const ahead = typeof entry.wentAheadOf === "number" && entry.wentAheadOf > 0 ? entry.wentAheadOf : 0;
  if (ahead === 0) return head;
  return `${head} · went ahead of the ${String(ahead)} waiting session ${ahead === 1 ? "item" : "items"}`;
}

/** "Stopped at 4:12 PM · goes first" for a run put back at the front; null without a readable time. Pure; exported for tests. */
export function requeuedText(requeuedAt: string | undefined, now?: number): string | null {
  const when = localTimeOf(requeuedAt, now === undefined ? {} : { now });
  return when ? `Stopped ${when} · goes first` : null;
}

/** The running entry's line: the app's activity phrase, else its phase, else "Starting". */
function activityOf(entry: GroomingQueueEntry): string {
  const activity = entry.activity?.trim();
  if (activity) return activity;
  if (entry.phase) return RUN_PHASE_LABELS[entry.phase];
  return "Starting…";
}

/** Seconds since `startedAt`, re-rendering only this element once a second. */
export function QueueElapsed({ startedAt }: { startedAt: string | null }) {
  const start = startedAt ? Date.parse(startedAt) : Number.NaN;
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, []);
  const seconds = Number.isNaN(start) ? 0 : Math.max(0, (now - start) / 1000);
  return (
    <span className="font-mono text-xs tabular-nums text-muted-foreground" data-testid="queue-elapsed">
      {formatElapsed(seconds)}
    </span>
  );
}

const iconButton = "h-8 w-8 p-0 md:h-8";

export interface QueueEntryRowProps {
  entry: GroomingQueueEntry;
  section: QueueSection;
  /** Up next: the entry's index in Up next (0 = next to run). */
  index?: number;
  /** Up next: how many entries are in Up next, to disable Move down on the last. */
  count?: number;
  /** Up next: shown under its leader as "Also in session". */
  follower?: boolean;
  actions: QueueRowActions;
  /** True while an action on this row waits for the app. */
  busy?: boolean;
  /** True for the row "See it in the Queue" opened (E290, frame B4): ringed and scrolled into view. */
  highlighted?: boolean;
}

export function QueueEntryRow(props: QueueEntryRowProps) {
  const { entry, section } = props;
  if (section === "completed" && isResultEntry(entry)) {
    return entry.kind === "author-questions" ? (
      <AuthorQuestionsResultCard entry={entry} />
    ) : (
      <BriefResultCard entry={entry} />
    );
  }
  return <QueueStatusRow {...props} />;
}

/** A running, Up next or finished-without-a-result row. */
function QueueStatusRow({
  entry,
  section,
  index = 0,
  count = 0,
  follower = false,
  actions,
  busy = false,
  highlighted = false,
}: QueueEntryRowProps) {
  const { title, idOnly } = useEntryTitle(entry);
  const rowRef = useRef<HTMLLIElement>(null);
  useEffect(() => {
    const row = rowRef.current;
    if (highlighted && row && typeof row.scrollIntoView === "function") row.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [highlighted]);
  const itemLink = entry.itemId ? groomingPath(entry.project, `items/${entry.itemId}`) : null;
  const nextInLine = section === "upNext" && entry.nextInLine === true;
  const requeued = section === "upNext" ? requeuedText(entry.requeuedAt) : null;

  return (
    <li
      className={cn(
        "group flex items-center gap-3 rounded-[10px] border border-[#1F1F23] bg-[#111113] px-4 py-3",
        follower && "ml-8 border-dashed bg-transparent py-2",
        nextInLine && "border-[#23A4FF]/30",
        entry.state === "failed" && "border-[#F87171]/30",
        highlighted && "ring-2 ring-[#23A4FF]/60 transition-shadow"
      )}
      ref={rowRef}
      data-testid="queue-entry"
      data-highlighted={highlighted ? "true" : undefined}
      data-entry-id={entry.id}
      data-state={entry.state}
      data-section={section}
      data-next-in-line={nextInLine ? "true" : undefined}
    >
      {section === "upNext" && (
        <span
          className={cn(
            "flex-none font-mono text-xs",
            nextInLine
              ? "flex h-6 min-w-6 items-center justify-center rounded-md bg-[#1E3A5F] px-1.5 font-semibold text-[#BFE3FF]"
              : "w-6 text-right text-muted-foreground"
          )}
          aria-label={`Place ${String(index + 1)}`}
        >
          {index + 1}
        </span>
      )}
      {section === "running" && (
        <span
          className="h-3.5 w-3.5 flex-none animate-spin rounded-full border-2 border-primary border-r-transparent"
          aria-hidden="true"
        />
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex min-w-0 items-baseline gap-2">
          {follower && (
            <span className={cn("flex-none text-[11px] font-medium uppercase tracking-wide", TOOLBOX_TEXT.faint)}>
              Also in session
            </span>
          )}
          {!(idOnly && itemLink) && (
            <span className={cn("truncate text-sm text-foreground", nextInLine && "font-semibold")}>{title}</span>
          )}
          {itemLink && (
            <Link to={itemLink} className="flex-none font-mono text-xs text-link hover:underline">
              AB#{entry.itemId}
            </Link>
          )}
          {nextInLine && (
            <span
              className="inline-flex flex-none items-center gap-1 self-center rounded-full border border-[#23A4FF]/30 bg-[#23A4FF]/10 px-2 py-0.5 text-[11px] font-medium text-[#BFE3FF]"
              data-testid="queue-next-in-line"
            >
              <Zap className="h-3 w-3" aria-hidden="true" />
              Next in line
            </span>
          )}
          {requeued && (
            <span
              className="inline-flex flex-none items-center self-center rounded-full border border-amber-500/30 bg-amber-500/[0.08] px-2 py-0.5 text-[11px] font-medium text-[#FCD34D]"
              data-testid="queue-requeued"
            >
              {requeued}
            </span>
          )}
        </div>
        {nextInLine && (
          <span className="text-xs text-muted-foreground" data-testid="queue-next-in-line-text">
            {nextInLineText(entry)}
          </span>
        )}
        <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
          <span className="flex-none">{QUEUE_KIND_LABELS[entry.kind]}</span>
          <span aria-hidden="true">·</span>
          <span className="flex-none">{entry.project}</span>
          {section === "running" && (
            <>
              <span aria-hidden="true">·</span>
              <span className="truncate" data-testid="queue-activity">
                {activityOf(entry)}
              </span>
            </>
          )}
        </div>
        {section === "completed" && <CompletedLine entry={entry} />}
      </div>

      {section === "running" && (
        <>
          <QueueElapsed startedAt={entry.startedAt} />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => {
              actions.stop(entry.id);
            }}
            aria-label={`Stop ${title}`}
          >
            <Square className="mr-1.5 h-3 w-3" aria-hidden="true" />
            Stop
          </Button>
        </>
      )}

      {section === "upNext" && (
        <div className="flex flex-none items-center gap-1">
          {!nextInLine && index > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs opacity-0 focus-visible:opacity-100 group-focus-within:opacity-100 group-hover:opacity-100 md:h-7"
              disabled={busy}
              onClick={() => {
                actions.move(entry.id, 0);
              }}
              aria-label={`Move ${title} to the top`}
            >
              <ArrowUpToLine className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
              Move to top
            </Button>
          )}
          {!nextInLine && (
            <>
              <Button
                type="button"
                variant="ghost"
                className={iconButton}
                disabled={busy || index === 0}
                onClick={() => {
                  actions.move(entry.id, index - 1);
                }}
                aria-label={`Move ${title} up`}
              >
                <ArrowUp className="h-4 w-4" aria-hidden="true" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                className={iconButton}
                disabled={busy || index >= count - 1}
                onClick={() => {
                  actions.move(entry.id, index + 1);
                }}
                aria-label={`Move ${title} down`}
              >
                <ArrowDown className="h-4 w-4" aria-hidden="true" />
              </Button>
            </>
          )}
          <Button
            type="button"
            variant="ghost"
            className={iconButton}
            disabled={busy}
            onClick={() => {
              actions.remove(entry.id);
            }}
            aria-label={`Remove ${title}`}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      )}

      {section === "completed" && (entry.state === "failed" || entry.state === "stopped") && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => {
            actions.retry(entry.id);
          }}
          aria-label={`Retry ${title}`}
        >
          <RotateCcw className="mr-1.5 h-3 w-3" aria-hidden="true" />
          Retry
        </Button>
      )}
    </li>
  );
}

/** The outcome line of a finished entry that is not a result card. */
function CompletedLine({ entry }: { entry: GroomingQueueEntry }) {
  switch (entry.state) {
    case "briefed":
    case "done":
      // Briefs and author questions with an item are result cards; this is a product-areas draft (or an entry without an item).
      return (
        <Link
          to={queueEntryLink(entry)}
          className="text-xs font-medium text-green-400 hover:underline"
          data-testid="queue-outcome"
        >
          {entry.kind === "product-areas" ? "Product areas draft ready" : FINISHED_TEXT}
        </Link>
      );
    case "not-ready":
      return (
        <span className={cn("text-xs", TOOLBOX_TEXT.faint)} data-testid="queue-outcome">
          Not ready for grooming{entry.message ? ` · ${entry.message}` : ""}
        </span>
      );
    case "failed":
      return (
        <span className="text-xs text-[#F87171]" data-testid="queue-outcome">
          Failed · {entry.message ?? groomingErrorMessage(entry.errorCode ?? "UNKNOWN").cause}
        </span>
      );
    case "stopped":
      return (
        <span className={cn("text-xs", TOOLBOX_TEXT.faint)} data-testid="queue-outcome">
          Stopped by you
        </span>
      );
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Result cards (frame 1c)
// ---------------------------------------------------------------------------

/**
 * The batch item and its brief for a finished brief entry, from the batch
 * read (`useGroomingBatch`: a database read, no ADO call; React Query shares
 * it between entries of the same batch). Null while it loads, when it fails
 * or is not allowed, and for an entry without a batch.
 */
function useEntryBrief(entry: GroomingQueueEntry): GroomingBatchItemView | null {
  const batch = useGroomingBatch(entry.batchId ?? undefined);
  const items = batch.data?.items ?? [];
  return (
    items.find((item) => entry.batchItemId !== null && item.id === entry.batchItemId) ??
    items.find((item) => item.itemId === entry.itemId) ??
    null
  );
}

/** Stop a click or key on an inner control from also opening the row. */
function stopRowOpen(event: MouseEvent | KeyboardEvent): void {
  event.stopPropagation();
}

const PRIMARY_LINK =
  "inline-flex h-[30px] flex-none items-center gap-1.5 whitespace-nowrap rounded-lg bg-primary px-3 text-[13px] font-semibold text-primary-foreground hover:bg-primary/90";
const SECONDARY_LINK =
  "inline-flex h-[30px] flex-none items-center gap-1.5 whitespace-nowrap rounded-lg border border-[#27272A] bg-[#1A1A1E] px-2.5 text-[13px] font-medium text-foreground hover:bg-[#222227]";

/**
 * The card frame shared by both result cards: a list item holding one
 * clickable row (role link, in the tab order, Enter or Space opens it). Inner
 * links stop the click so a press opens the item once.
 */
function ResultCardFrame({
  entry,
  title,
  icon,
  line2,
  chip,
  action,
}: {
  entry: GroomingQueueEntry;
  title: EntryTitle;
  icon: ReactNode;
  line2: ReactNode;
  chip: ReactNode;
  action: ReactNode;
}) {
  const navigate = useNavigate();
  const to = queueEntryLink(entry);
  const open = () => {
    void navigate(to);
  };
  const label = title.idOnly ? `Open ${title.title}` : `Open AB#${entry.itemId ?? ""} ${title.title}`;
  return (
    <li data-testid="queue-entry" data-entry-id={entry.id} data-state={entry.state} data-section="completed">
      <div
        role="link"
        tabIndex={0}
        aria-label={label}
        className="flex cursor-pointer items-center gap-4 rounded-[10px] border border-[#1F1F23] bg-[#111113] px-4 py-3.5 hover:border-[#27272A] hover:bg-[#141417] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        data-testid="queue-result-card"
        onClick={open}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            open();
          }
        }}
      >
        {icon}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex min-w-0 items-center gap-2">
            {!title.idOnly && (
              <span className="flex-none font-mono text-xs text-muted-foreground">AB#{entry.itemId}</span>
            )}
            <span className="truncate text-sm font-semibold text-foreground" data-testid="queue-result-title">
              {title.title}
            </span>
          </div>
          <div
            className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-xs"
            data-testid="queue-result-line"
          >
            {line2}
          </div>
        </div>
        {chip}
        {action}
      </div>
    </li>
  );
}

function Dot() {
  return (
    <span className={TOOLBOX_TEXT.faint} aria-hidden="true">
      ·
    </span>
  );
}

/** A finished brief: "Brief · 11:42 AM · About 3 points, could be 2–5 · Not sure yet", its state and Open brief. */
function BriefResultCard({ entry }: { entry: GroomingQueueEntry }) {
  const item = useEntryBrief(entry);
  const title = useEntryTitle(entry, item?.title);
  const brief = item?.brief ?? null;
  const time = finishedTimeText(entry.finishedAt);
  return (
    <ResultCardFrame
      entry={entry}
      title={title}
      icon={<FileCheck className="h-5 w-5 flex-none text-[#4ADE80]" aria-hidden="true" />}
      line2={
        <>
          <span className="text-muted-foreground">
            {QUEUE_KIND_LABELS[entry.kind]}
            {time ? ` · ${time}` : ""}
          </span>
          {brief && (
            <>
              <Dot />
              <span className="text-foreground" data-testid="queue-result-size">
                {briefSizeText(brief)}
              </span>
              <Dot />
              <span className="text-muted-foreground" data-testid="queue-result-confidence">
                {confidenceWords(brief.confidence)}
              </span>
            </>
          )}
        </>
      }
      chip={
        brief && item ? (
          <BriefStateChip
            state={briefStateOf({
              commentStatus: brief.commentStatus,
              reviewState: item.reviewState,
            })}
            className="flex-none"
          />
        ) : null
      }
      action={
        <Link to={queueEntryLink(entry)} className={PRIMARY_LINK} onClick={stopRowOpen} onKeyDown={stopRowOpen}>
          <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          Open brief
        </Link>
      }
    />
  );
}

/** True when this computer posted the questions after the run finished (or the finish time is unknown). Pure; exported for tests. */
export function postedSinceFinish(postedAt: string | null, finishedAt: string | null): boolean {
  if (postedAt === null) return false;
  const posted = Date.parse(postedAt);
  if (Number.isNaN(posted)) return false;
  const finished = finishedAt === null ? Number.NaN : Date.parse(finishedAt);
  return Number.isNaN(finished) || posted >= finished;
}

/** A finished author-questions draft: "Questions for the author · 11:01 AM · 4 questions drafted, not posted" and Open item. */
function AuthorQuestionsResultCard({ entry }: { entry: GroomingQueueEntry }) {
  const title = useEntryTitle(entry);
  // Only a draft read this page already holds: the read checks the item live in ADO (no per-card ADO read).
  const draft = useAuthorQuestionsDraft(entry.project, entry.itemId ?? undefined, {
    cacheOnly: true,
  });
  const count = draft.data ? draft.data.questions.length : null;
  const posted = postedSinceFinish(questionsPostedAt(entry.key), entry.finishedAt);
  const time = finishedTimeText(entry.finishedAt);
  return (
    <ResultCardFrame
      entry={entry}
      title={title}
      icon={<MessageCircleQuestion className="h-5 w-5 flex-none text-[#FBBF24]" aria-hidden="true" />}
      line2={
        <span className="text-muted-foreground" data-testid="queue-result-questions">
          {AUTHOR_QUESTIONS_FALLBACK_TITLE}
          {time ? ` · ${time}` : ""} · {questionsDraftedText(count, posted)}
        </span>
      }
      chip={
        <span className="flex-none">
          <StageChip stage="needs-information" />
        </span>
      }
      action={
        <Link to={queueEntryLink(entry)} className={SECONDARY_LINK} onClick={stopRowOpen} onKeyDown={stopRowOpen}>
          <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          Open item
        </Link>
      }
    />
  );
}
